import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { BillingHistoryItem } from "@/features/billing/billing.types";

type CreditHistoryListProps = {
  currentBalance: number;
  history: BillingHistoryItem[];
};

function escapeCsvValue(value: string) {
  return `"${value.replaceAll("\"", "\"\"")}"`;
}

export function CreditHistoryList({ currentBalance, history }: CreditHistoryListProps) {
  const { t } = useTranslation();
  let runningBalance = currentBalance;
  const entries = history.map((entry) => {
    const balanceAfter = runningBalance;
    runningBalance -= entry.amount;

    return {
      ...entry,
      balanceAfter,
    };
  });

  const handleExport = () => {
    if (typeof window === "undefined" || entries.length === 0) {
      return;
    }

    const csvRows = [
      ["date", "label", "amount", "balance_after"],
      ...entries.map((entry) => [
        new Date(entry.createdAt).toISOString(),
        t(`billing.history.labels.${entry.feature}`, {
          defaultValue: entry.feature,
        }),
        String(entry.amount),
        String(entry.balanceAfter),
      ]),
    ];

    const csv = csvRows
      .map((row) => row.map((cell) => escapeCsvValue(cell)).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");

    link.href = url;
    link.download = `billing-history-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-ink/78">
            {t("billing.history.eyebrow")}
          </p>
          <h3 className="text-[2rem] font-semibold leading-tight text-ink">
            {t("billing.history.title")}
          </h3>
        </div>

        <Button
          className="rounded-full px-0 text-base font-medium text-ink/70 shadow-none hover:bg-transparent hover:text-ink"
          disabled={entries.length === 0}
          onClick={handleExport}
          type="button"
          variant="ghost"
        >
          <Download className="size-5" />
          {t("billing.history.export", { defaultValue: "Export" })}
        </Button>
      </div>

      {entries.length === 0 ? (
        <div className="rounded-[1.5rem] border border-dashed border-[#ddd2c4] px-5 py-7 text-sm text-ink/60">
          {t("billing.history.empty")}
        </div>
      ) : (
        <div className="divide-y divide-[#ece2d6] border-y border-[#ece2d6]">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="flex flex-col gap-4 py-6 sm:flex-row sm:items-start sm:justify-between"
            >
              <div>
                <p className="text-[1.05rem] font-semibold text-ink">
                  {t(`billing.history.labels.${entry.feature}`, {
                    defaultValue: entry.feature,
                  })}
                </p>
                <p className="mt-1 text-sm text-ink/54">
                  {t("billing.dateValue", { date: entry.createdAt })}
                </p>
              </div>

              <div className="text-left sm:text-right">
                <p className={`text-[1.15rem] font-semibold ${
                  entry.amount < 0 ? "text-clay" : "text-[#0f9f6f]"
                }`}>
                  {entry.amount > 0 ? "+" : ""}
                  {entry.amount}
                </p>
                <p className="mt-1 text-sm text-ink/54">
                  {t("billing.history.balanceAfter", {
                    count: entry.balanceAfter,
                    defaultValue: "Balance: {{count}}",
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
